import { QueryClient, type QueryFunction } from "@tanstack/react-query";
import { api } from "./api";
import { QUERY_CACHE_MAX_AGE_MS } from "./queryPersist";
// The standalone toast (not the useToast hook) — this file is a module, not a
// component, and the shadcn toast store is deliberately usable outside React.
import { toast } from "@/hooks/use-toast";

export function isGuestMode(): boolean {
  return sessionStorage.getItem("trybeup_guest") === "true";
}

/** GET a JSON endpoint through the `api` client — the body of the default
 *  queryFn, for queries that build their URL from more than queryKey[0]
 *  (lib/dayRecord.ts adds the phone's date). */
export async function getJson<T = unknown>(url: string): Promise<T> {
  const res = await api.get(url);
  // A 2xx that isn't JSON is not data (Sentry 2026-09-17, web/Firefox:
  // "K1.reduce is not a function" — /api/dm/conversations came back as an
  // HTML page with status 200, the string reached `conversations.reduce`
  // and the whole app fell into the error boundary). Treat it as a failed
  // request so callers keep their `= []` defaults and retry applies.
  const contentType = String(res.headers?.["content-type"] ?? "");
  if (typeof res.data === "string" && (contentType.includes("text/html") || /^\s*<(!doctype|html)/i.test(res.data))) {
    const err = new Error(`Non-JSON response for ${url}`) as Error & { status?: number };
    err.status = 502;
    throw err;
  }
  return res.data as T;
}

const defaultQueryFn: QueryFunction = async ({ queryKey }) => getJson(queryKey[0] as string);

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryFn: defaultQueryFn,
      refetchInterval: false,
      // OFF. Was `true` (2026-06-13) to give "same-session cross-device sync"
      // — switching from the desktop tab to the phone re-fired every active
      // query so fresh data arrived without a manual reload. The cost was
      // brutal on mobile: every alt-tab / lock-screen unlock / push-notification
      // glance kicked off 15+ parallel requests on the home tab and the user
      // watched spinners spin (founder report 2026-06-14, "loading times are
      // still slow"). The cross-device sync we gain isn't worth that, and the
      // 60s staleTime + refetchOnMount still pick up fresh data the moment the
      // user navigates anywhere inside the app.
      refetchOnWindowFocus: false,
      staleTime: 60 * 1000,       // 1 minute — queries are considered fresh
      // Unused cache entries are garbage collected after this. Must be >= the
      // persisted cache's maxAge (24h, lib/queryPersist.ts): it was 30 min, so
      // any query no screen was watching was dropped from memory — and from
      // the next save to disk — half an hour after the user left that screen,
      // and the "instant last-known data" never reached most of the app on
      // the next open (review 2026-10-13).
      gcTime: QUERY_CACHE_MAX_AGE_MS,
      // Retry transient failures on flaky mobile networks (max 3 attempts
      // total: original + 2 retries). Only retry on network errors or 5xx
      // server errors — 4xx (401 Unauthorized, 403 Forbidden, 404 Not
      // Found, 422 Unprocessable) are real client-side errors that should
      // surface immediately, not be hammered. Without this, every brief
      // server hiccup or phone-handing-over-the-tunnel produced a visible
      // error toast on mobile.
      retry: (failureCount, error) => {
        if (failureCount >= 2) return false;
        // Axios attaches the response on err.response; fetch-thrown errors
        // are typeof TypeError. Treat both anything-without-a-status and
        // any 5xx as transient.
        const e = error as { response?: { status?: number }; status?: number };
        const status = e?.response?.status ?? e?.status;
        if (!status) return true;        // network error — retry
        return status >= 500 && status < 600;  // server error — retry
      },
      retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 4000),  // 1s → 2s → 4s
    },
    mutations: {
      // Mutations stay at retry:false. Auto-retrying a non-idempotent POST
      // (create task / habit / etc.) could double-create resources. The
      // user retries manually if a mutation fails.
      retry: false,

      // A floor under every mutation that doesn't handle its own failures.
      //
      // Audit 2026-09-04: 82 of 349 useMutation call sites had no onError,
      // and there was no global handler either — so those failures were
      // completely silent. The user taps, the request fails, and nothing
      // happens at all. No toast, no message, no spinner ending in anything.
      // "I pressed it and nothing happened" is the least debuggable report
      // there is, and the app was manufacturing it.
      //
      // This is a DEFAULT, not a MutationCache listener, and that distinction
      // is the whole design. defaultMutationOptions does
      // `{...defaultOptions.mutations, ...options}` (verified in
      // query-core/queryClient.cjs), so a mutation's own onError REPLACES
      // this one. The 267 call sites that already report failures keep their
      // own wording and show exactly one toast. A MutationCache handler would
      // have fired alongside them and doubled every existing message.
      //
      // Deliberately vague wording: this fires for anything from a dropped
      // connection to a 500, and a specific claim would be wrong most of the
      // time. Anywhere the user deserves better than this, the fix is an
      // onError on that mutation — which now also silences this.
      onError: (error: unknown) => {
        const e = error as { response?: { status?: number }; status?: number };
        const status = e?.response?.status ?? e?.status;
        // 401 is handled globally by the auth layer (it redirects to sign-in).
        // A toast on top of that just adds noise to an already-jarring moment.
        if (status === 401) return;
        toast({
          title: "That didn't save",
          description: "Check your connection and try again.",
          variant: "destructive",
        });
      },
    },
  },
});

/**
 * Legacy apiRequest — kept for compatibility with client code that uses it.
 * Client code does: const res = await apiRequest("POST", "/api/...", data)
 * Then: const result = await res.json()
 */
export async function apiRequest(
  method: string,
  url: string,
  data?: unknown
): Promise<Response> {
  const { apiFetch } = await import("./api");
  const options: RequestInit = {
    method,
    headers: data ? { "Content-Type": "application/json" } : {},
    body: data ? JSON.stringify(data) : undefined,
  };
  return apiFetch(url, options);
}

/**
 * Compatibility: client code may import getQueryFn from queryClient.
 * Our defaultQueryFn already returns null on 401, so this is a passthrough.
 */
export function getQueryFn(_options?: { on401?: string }) {
  return defaultQueryFn;
}
